import { useState, useEffect, useRef, forwardRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { OnboardingStepProps } from '@/types/components/onboarding'
import { WelcomeSection } from './WelcomeSection'
import { AudiencesSection } from './AudiencesSection'
import { ChatSection } from './ChatSection'
import { StudiesSection } from './StudiesSection'
import { ALL_TWIN_IMAGES } from './TwinPortraitStack'
import { computeJtbdStudyIndex } from './jtbdStudyIndex'

/**
 * Section 0 is the welcome screen with its own embedded CTA — no bottom nav.
 * Sections 1-3 use the shared Previous / Next bottom navigation bar.
 */
const TOTAL_SECTIONS = 4
const WELCOME_SECTION = 0
const LAST_SECTION = TOTAL_SECTIONS - 1

export const GettingStartedGuide = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function GettingStartedGuide({ wizardState, onNext, className }, ref) {
    const { t } = useTranslation('onboarding')
    const [section, setSection] = useState(WELCOME_SECTION)
    const shouldReduceMotion = useReducedMotion()
    const duration = shouldReduceMotion ? 0 : 0.25

    // Eagerly warm the browser cache so twin portraits are instant by section 2
    const preloaded = useRef(false)
    useEffect(() => {
      if (preloaded.current) return
      preloaded.current = true
      ALL_TWIN_IMAGES.forEach((url) => {
        const img = new Image()
        img.src = url
      })
    }, [])

    // Compute the personalized study carousel start index from JTBD selections.
    // Memoized so it doesn't change between sections.
    const studiesInitialIndex = useMemo(
      () => computeJtbdStudyIndex(wizardState.selectedJtbd),
      [wizardState.selectedJtbd],
    )

    const handleNextSection = useCallback(() => {
      if (section === LAST_SECTION) {
        onNext()
        return
      }
      setSection((s) => s + 1)
    }, [section, onNext])

    // Advance from the welcome screen to section 1 (Audiences)
    const handleWelcomeNext = useCallback(() => {
      setSection(1)
    }, [])

    const isWelcomeSection = section === WELCOME_SECTION
    const isLast = section === LAST_SECTION

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col gap-6 outline-none', className)}>
        <AnimatePresence mode="wait">
          <motion.div
            key={section}
            initial={{ opacity: 0, x: isWelcomeSection ? 0 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isWelcomeSection ? 0 : -20 }}
            transition={{ duration, ease: 'easeOut' }}
          >
            {section === 0 && (
              <WelcomeSection
                orgName={wizardState.orgName}
                isInvitedUser={wizardState.isInvitedUser}
                onNext={handleWelcomeNext}
              />
            )}
            {section === 1 && <AudiencesSection wizardState={wizardState} />}
            {section === 2 && (
              <ChatSection
                targetingType={wizardState.targetingType}
                wizardState={wizardState}
              />
            )}
            {section === 3 && <StudiesSection initialIndex={studiesInitialIndex} />}
          </motion.div>
        </AnimatePresence>

        {/* Bottom navigation — hidden on welcome section (it has its own CTA) */}
        {!isWelcomeSection && (
          <div className="flex items-center justify-between">
            <Text size="sm" color="muted" data-testid="guide-section-indicator">
              {t('guide.sectionOf', { current: section, total: TOTAL_SECTIONS - 1 })}
            </Text>

            <Button
              data-testid={isLast ? 'guide-get-started-button' : 'guide-next-button'}
              variant="cta"
              size="sm"
              onClick={handleNextSection}
              className="whitespace-nowrap"
            >
              {isLast ? t('guide.getStarted') : t('guide.next')}
              {!isLast && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
          </div>
        )}
      </div>
    )
  },
)
